import { Box, 
         Drawer, 
         IconButton, 
         InputAdornment, 
         TextField, 
         Typography, 
         useTheme,
         List,
         ListItem,
         ListItemText,
         Chip
       }                                    from "@mui/material"
import React, 
       { useMemo, 
         useState 
       }                                    from "react"
import { useSelector }                      from "react-redux"
import { selectPublishedQuestions }         from "store/dataReducers/surveyQBSlice"
import { APIData }                          from "types/cms/frontendTypes"
import { X, Search }                        from "lucide-react"
import { BglPrimarySmallButton }            from "components/button/BglButtons"
import { getLocalizedQuestionTitle }        from "../utils/SurveyTemplateLocalizationUtils"
import { CMS_DEFAULT_LOCALE }               from "store/dataReducers/cmsPilotSlice"

interface ImportQuestionBankDrawerProps {
  open: boolean
  onClose: () => void
  onAddQuestion: (question: APIData<'api::survey-question.survey-question'>) => void
  existingQuestionIds: Set<string>
  mode?: 'primary' | 'child'
  childQuestionContext?: {
    parentQuestionUid: string
    parentChoiceValue: string
    parentChoiceTitle: string
    parentChoiceReadMapper: string
  }
  selectedLocale: string
}

export const ImportQuestionBankDrawer = ({
  open,
  onClose,
  onAddQuestion,
  existingQuestionIds,
  mode = 'primary',
  childQuestionContext = undefined,
  selectedLocale,
}: ImportQuestionBankDrawerProps) => {
  const theme = useTheme()
  const publishedQuestions = useSelector(selectPublishedQuestions)
  const [searchText, setSearchText] = useState('')

  // Filter questions based on search text (search in localized titles)
  const filteredQuestions = useMemo(() => {
    if (!searchText.trim()) {
      return publishedQuestions
    }
    
    const searchLower = searchText.toLowerCase().trim()
    return publishedQuestions.filter(question => {
      const localizedTitle = getLocalizedQuestionTitle(question, selectedLocale)
      return localizedTitle?.toLowerCase().includes(searchLower)
    })
  }, [publishedQuestions, searchText, selectedLocale])

  const handleAddQuestion = (question: APIData<'api::survey-question.survey-question'>) => {
    onAddQuestion(question)
  }

  const drawerWidth = 600

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          // Override theme hover effects for ListItem in this drawer
          '& .MuiListItem-root': {
            '&:hover': {
              backgroundColor: 'transparent !important',
              color: 'inherit !important',
            },
            '&.Mui-selected': {
              backgroundColor: 'transparent !important',
              color: 'inherit !important',
              '&:hover': {
                backgroundColor: 'transparent !important',
              },
            },
          },
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          bgcolor: theme.palette.background.paper,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            pb : 1,
          }}
        >
          <Typography variant="h6">
            {mode === 'child' ? `Add Child Question to : ${childQuestionContext?.parentChoiceTitle} (${childQuestionContext?.parentChoiceReadMapper})`  : 'Import from Question Bank'}
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: theme.palette.text.secondary,
              '&:hover': {
                bgcolor: theme.palette.action.hover,
              },
            }}
          >
            <X size={20} />
          </IconButton>
        </Box>

        {/* Info text */}
        <Box sx={{ px: 2, pb: 2, mb: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Typography sx={{ color: theme.palette.text.primary, fontSize: '14px' }}>
            {mode === 'child' 
              ? ''
              : 'Only PUBLISHED questions are available.'}
          </Typography>
        </Box>

        {/* Search bar */}
        <Box sx={{ px: 2, pb: 2 }}>
          <TextField
            fullWidth
            placeholder="Search question bank..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={18} style={{ color: theme.palette.text.secondary }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: theme.palette.background.default,
              },
            }}
          />
        </Box>

        {/* Questions list */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            px: 2,
            py: 1,
          }}
        >
          {filteredQuestions.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: 'center', py: 4}}
            >
              {searchText.trim() 
                ? 'No questions found matching your search.' 
                : 'No published questions available.'}
            </Typography>
          ) : (
            <List sx={{ py: 0 }}>
              {filteredQuestions.map((question) => {
                // Determine if question should be disabled based on mode and maxLevel
                const isDisabled = (() => {
                  // For primary mode: disable if already exists
                  if (mode === 'primary') {
                    return existingQuestionIds.has(question.documentId)
                  }
                  
                  // For child mode: 
                  // - If maxLevel <= 1: disable if already exists (can only be child of one parent)
                  // - If maxLevel > 1: allow even if exists (can be child of multiple parents)
                  if (mode === 'child') {
                    const questionMaxLevel = question.maxLevel ?? 1
                    if (questionMaxLevel <= 1) {
                      return existingQuestionIds.has(question.documentId)
                    }
                    // maxLevel > 1: always allow (even if already exists)
                    return false
                  }
                  
                  return false
                })()
                
                // Check if localized title exists
                const localizedTitle = selectedLocale === CMS_DEFAULT_LOCALE 
                  ? question.title
                  : (question as any).localizations?.find((l: any) => l.locale === selectedLocale)?.title
                
                const hasLocalizedTitle = localizedTitle !== undefined && localizedTitle !== null
                const displayTitle = hasLocalizedTitle 
                  ? localizedTitle 
                  : question.title // Default locale title
                const isDefaultTitle = !hasLocalizedTitle && selectedLocale !== CMS_DEFAULT_LOCALE
                
                return (
                  <ListItem
                    key={question.documentId}
                    sx={{
                      px: 1,
                      py: 1.5,
                      mb: 1,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 2,
                      bgcolor: theme.palette.background.paper,
                      // Explicitly override any hover effects
                      '&:hover': {
                        bgcolor: theme.palette.background.paper,
                        color: 'inherit',
                      },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            color: isDefaultTitle ? theme.palette.text.primary : theme.palette.text.secondary,
                            pr: 2,
                            fontStyle: isDefaultTitle ? 'italic' : 'normal',
                          }}
                        >
                          {displayTitle}
                        </Typography>
                      }
                    />
                    <Box sx={{ flexShrink: 0 }}>
                      <BglPrimarySmallButton
                        disabled={isDisabled}
                        onClick={() => handleAddQuestion(question)}
                        sx={{ minWidth: 80 }}
                      >
                        Add
                      </BglPrimarySmallButton>
                    </Box>
                  </ListItem>
                )
              })}
            </List>
          )}
        </Box>
      </Box>
    </Drawer>
  )
}
